/**
 * SettleUpPanel — iter194.
 *
 * One-tap settlement controls embedded inside ArchivedTripBanner. Only
 * rendered when the group is an archived Trip and the current user has
 * an open settle-pair involving them.
 *
 * Two-step workflow (creditor is final authority):
 *   - Debtor taps "Send ₹X" → creates a pending settle_send transfer.
 *     The creditor must Mark Received to actually move the balance.
 *   - Creditor's "Mark Received":
 *       - if a pending Send exists → approves it (in-app flow)
 *       - if no pending Send → records an offline settlement (cash flow)
 *
 * Live status is broadcast to BOTH parties via SSE — the panel refetches
 * its state on every relevant event so each device stays in sync.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Send, BellRing, CheckCircle2, Clock, XCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useRealtime } from '../context/RealtimeContext';

const SETTLE_EVENT_TYPES = [
  'settle_send_initiated',
  'settle_received',
  'settle_cancelled',
  'settle_nudge',
];

function formatINR(amount) {
  const n = Math.round(Math.abs(Number(amount) || 0));
  return `₹${n.toLocaleString('en-IN')}`;
}

function nudgeCooldownLabel(lastIso) {
  if (!lastIso) return null;
  const last = new Date(lastIso).getTime();
  if (Number.isNaN(last)) return null;
  const elapsed = Date.now() - last;
  const SIX_H = 6 * 60 * 60 * 1000;
  if (elapsed >= SIX_H) return null;
  const remaining = SIX_H - elapsed;
  const hours = Math.floor(remaining / (60 * 60 * 1000));
  const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export default function SettleUpPanel({ group, currentUserId }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null); // disables a row while its action is in flight
  const { onEvent } = useRealtime();
  const groupId = group?.id;
  const reloadTimer = useRef(null);

  const fetchState = useCallback(async () => {
    if (!groupId) return;
    try {
      const res = await api.get(`/groups/${groupId}/settle-up/state`);
      setState(res.data);
    } catch (e) {
      // 400 = active (non-archived) trip — panel shouldn't render.
      if (e?.response?.status !== 400) {
        console.warn('settle-up state fetch failed', e?.response?.data || e);
      }
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  // Live: refetch on any settle SSE event for this group.
  useEffect(() => {
    if (!groupId) return undefined;
    const off = onEvent(SETTLE_EVENT_TYPES, (type, payload) => {
      if (payload?.group_id && payload.group_id !== groupId) return;
      // Toast a friendly status update for the OTHER party (not the actor).
      const involvesMe =
        payload?.from_user_id === currentUserId ||
        payload?.to_user_id === currentUserId ||
        payload?.creditor_id === currentUserId ||
        payload?.debtor_id === currentUserId;
      if (involvesMe && payload?.message) {
        if (type === 'settle_received') toast.success('✅ ' + payload.message);
        else if (type === 'settle_send_initiated') toast.info('💸 ' + payload.message);
        else if (type === 'settle_cancelled') toast.warning('✖ ' + payload.message);
        else if (type === 'settle_nudge' && payload.debtor_id === currentUserId) toast.info('🔔 ' + payload.message);
      }
      // Debounce rapid bursts (e.g. two events fired together)
      if (reloadTimer.current) clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(() => fetchState(), 250);
    });
    return () => {
      off();
      if (reloadTimer.current) clearTimeout(reloadTimer.current);
    };
  }, [groupId, currentUserId, onEvent, fetchState]);

  const { myPayPairs, myReceivePairs, pendingByPeer } = useMemo(() => {
    const payPairs = [];
    const receivePairs = [];
    const pending = {};
    // In _compute_settle_pairs: from_user_id = positive balance member (CREDITOR),
    // to_user_id = negative balance member (DEBTOR). So:
    //   - I'm `from_user_id` → I'm the CREDITOR (owed) → "receive" side
    //   - I'm `to_user_id`   → I'm the DEBTOR (owe)    → "pay" side
    if (state?.pairs) {
      for (const p of state.pairs) {
        if (p.to_user_id === currentUserId) payPairs.push(p);
        else if (p.from_user_id === currentUserId) receivePairs.push(p);
      }
    }
    if (state?.pending_sends) {
      for (const t of state.pending_sends) {
        // settle_send transfer: from_user_id = debtor (initiator), to_user_id = creditor.
        // Key the pending by the OTHER party from MY perspective.
        const peerId = t.from_user_id === currentUserId ? t.to_user_id : t.from_user_id;
        if (!pending[peerId]) pending[peerId] = [];
        pending[peerId].push(t);
      }
    }
    return { myPayPairs: payPairs, myReceivePairs: receivePairs, pendingByPeer: pending };
  }, [state, currentUserId]);

  // ---------- Actions ----------
  const handleSend = async (pair) => {
    // I am the DEBTOR (pair.to_user_id === me). Recipient = creditor = pair.from_user_id.
    const key = `send:${pair.from_user_id}`;
    setBusyKey(key);
    try {
      await api.post('/coin-transfers/settle-send', {
        group_id: groupId,
        to_user_id: pair.from_user_id,
        amount: pair.amount,
      });
      toast.success(`Sent ₹${pair.amount.toLocaleString('en-IN')} — waiting for ${pair.from_name} to confirm`);
      fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not initiate send');
    } finally {
      setBusyKey(null);
    }
  };

  const handleCancelSend = async (transfer) => {
    const key = `cancel:${transfer.id}`;
    setBusyKey(key);
    try {
      await api.post(`/coin-transfers/${transfer.id}/cancel-settle-send`);
      toast.success('Send cancelled');
      fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel');
    } finally {
      setBusyKey(null);
    }
  };

  const handleMarkReceived = async (pair, pendingTransfer) => {
    // I am the CREDITOR (pair.from_user_id === me). Payer = debtor = pair.to_user_id.
    const key = `received:${pair.to_user_id}`;
    setBusyKey(key);
    try {
      if (pendingTransfer) {
        await api.post(`/coin-transfers/${pendingTransfer.id}/mark-received`);
        toast.success(`Confirmed ₹${Math.round(pendingTransfer.amount).toLocaleString('en-IN')} from ${pair.to_name}`);
      } else {
        await api.post('/coin-transfers/settle-mark-received-offline', {
          group_id: groupId,
          from_user_id: pair.to_user_id,
          amount: pair.amount,
        });
        toast.success(`Marked ₹${pair.amount.toLocaleString('en-IN')} as received from ${pair.to_name} (offline)`);
      }
      fetchState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark as received');
    } finally {
      setBusyKey(null);
    }
  };

  const handleNudge = async (pair) => {
    // I am the CREDITOR. Debtor I'm nudging = pair.to_user_id.
    const key = `nudge:${pair.to_user_id}`;
    setBusyKey(key);
    try {
      await api.post('/coin-transfers/settle-nudge', {
        group_id: groupId,
        debtor_user_id: pair.to_user_id,
      });
      toast.success(`Nudged ${pair.to_name}`);
      fetchState();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not send nudge';
      if (e?.response?.status === 429) toast.info('⏳ ' + detail);
      else toast.error(detail);
    } finally {
      setBusyKey(null);
    }
  };

  // ---------- Render guards ----------
  if (!groupId) return null;
  if (loading) {
    return (
      <div className="rounded-xl border border-rose-200 dark:border-rose-900/40 bg-white/60 dark:bg-rose-950/30 px-3 py-2.5 text-[12px] text-rose-800 dark:text-rose-200 flex items-center gap-2" data-testid="settle-panel-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading settle status…
      </div>
    );
  }
  if (!state) return null;

  const settled =
    myPayPairs.length === 0 &&
    myReceivePairs.length === 0 &&
    Object.keys(pendingByPeer).length === 0;

  if (settled) {
    return (
      <div
        className="rounded-xl border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2.5 text-[12px] font-semibold text-emerald-800 dark:text-emerald-200 flex items-center gap-2"
        data-testid="settle-panel-cleared"
      >
        <CheckCircle2 className="w-4 h-4" />
        All settled — you have no open balances in this trip.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="settle-up-panel">
      <div className="text-xs uppercase tracking-[0.16em] font-bold text-rose-900/80 dark:text-rose-200/80">
        Settle up
      </div>

      {/* Debtor side: pairs where I owe someone (I'm `to_user_id`, peer = `from_user_id`/creditor) */}
      {myPayPairs.map((p) => {
        const pending = (pendingByPeer[p.from_user_id] || []).find((t) => t.from_user_id === currentUserId);
        const sendKey = `send:${p.from_user_id}`;
        const cancelKey = `cancel:${pending?.id}`;
        return (
          <div
            key={`pay-${p.from_user_id}`}
            className="rounded-xl border border-rose-200 dark:border-rose-900/40 bg-white dark:bg-rose-950/30 px-3 py-2.5 flex items-center justify-between gap-3"
            data-testid={`settle-row-pay-${p.from_user_id}`}
          >
            <div className="min-w-0">
              <p className="text-[12px] font-bold text-rose-900 dark:text-rose-100 truncate">
                You owe {p.from_name} <span className="tabular-nums">{formatINR(p.amount)}</span>
              </p>
              {pending && (
                <p className="text-xs text-amber-700 dark:text-amber-300 inline-flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" /> Sent {formatINR(pending.amount)} — waiting for {p.from_name} to confirm
                </p>
              )}
            </div>
            {pending ? (
              <button
                type="button"
                onClick={() => handleCancelSend(pending)}
                disabled={busyKey === cancelKey}
                data-testid={`settle-cancel-send-${p.from_user_id}`}
                className="shrink-0 rounded-lg border border-rose-300 bg-white dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/40 active:scale-[0.99] transition px-2.5 py-1.5 text-xs font-bold flex items-center gap-1 disabled:opacity-50"
              >
                <XCircle className="w-3 h-3" /> Cancel
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleSend(p)}
                disabled={busyKey === sendKey}
                data-testid={`settle-send-${p.from_user_id}`}
                className="shrink-0 rounded-lg bg-rose-600 hover:bg-rose-700 active:scale-[0.99] transition px-3 py-1.5 text-xs font-bold text-white flex items-center gap-1.5 disabled:opacity-60"
              >
                {busyKey === sendKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Send {formatINR(p.amount)}
              </button>
            )}
          </div>
        );
      })}

      {/* Creditor side: pairs where someone owes me (I'm `from_user_id`, peer = `to_user_id`/debtor) */}
      {myReceivePairs.map((p) => {
        const pending = (pendingByPeer[p.to_user_id] || []).find((t) => t.to_user_id === currentUserId);
        const nudgeKey = `nudge:${p.to_user_id}`;
        const recvKey = `received:${p.to_user_id}`;
        const cooldown = nudgeCooldownLabel(state?.my_nudges?.[p.to_user_id]);
        return (
          <div
            key={`recv-${p.to_user_id}`}
            className={`rounded-xl border px-3 py-2.5 flex flex-col gap-2 ${pending
                ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30'
                : 'border-rose-200 dark:border-rose-900/40 bg-white dark:bg-rose-950/30'}`}
            data-testid={`settle-row-recv-${p.to_user_id}`}
          >
            <div className="min-w-0">
              <p className="text-[12px] font-bold text-rose-900 dark:text-rose-100 truncate">
                {p.to_name} owes you <span className="tabular-nums">{formatINR(p.amount)}</span>
              </p>
              {pending && (
                <p className="text-xs text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1 mt-0.5 font-semibold">
                  ⚡ {p.to_name} sent {formatINR(pending.amount)} — confirm receipt
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleNudge(p)}
                disabled={busyKey === nudgeKey || !!cooldown}
                data-testid={`settle-nudge-${p.to_user_id}`}
                className="flex-1 rounded-lg border border-rose-300 bg-white dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/40 active:scale-[0.99] transition px-2 py-1.5 text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
              >
                {busyKey === nudgeKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
                {cooldown ? `Nudge in ${cooldown}` : 'Nudge'}
              </button>
              <button
                type="button"
                onClick={() => handleMarkReceived(p, pending)}
                disabled={busyKey === recvKey}
                data-testid={`settle-mark-received-${p.to_user_id}`}
                className={`flex-1 rounded-lg active:scale-[0.99] transition px-2 py-1.5 text-xs font-bold text-white flex items-center justify-center gap-1 disabled:opacity-60 ${pending ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
              >
                {busyKey === recvKey ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                {pending ? 'Confirm receipt' : 'Mark received'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
